import React from 'react';
import {
  POPULAR_MAKES_EXTENDED,
  POPULAR_EV_MODELS,
} from '../../../lib/constants';
import { City, isCity, State } from '../../../lib/schema/geo/types';
import { SeoLocation } from '../../../lib/staticQueries';
import { slugify } from '../../../lib/utils';
import { FilterType } from '../../../types/filters';
import { ResponsiveWrapper } from '../../atoms/ResponsiveWrapper/ResponsiveWrapper';
import Accordion from '../../molecules/Accordion';

const locationSlug = (location: City | State) => {
  return isCity(location) ? location.slug : location.code;
};

interface RelatedLinkSectionProps {
  title: string;
  links: {
    href: string;
    label: string;
  }[];
}

const RelatedLinkSection: React.FC<RelatedLinkSectionProps> = ({
  title,
  links,
}) => {
  return (
    <div className="border-b border-neutral-200 pb-l last:border-b-0">
      <Accordion
        buttonClassName="text-neutral-900 text-h4Regular"
        icon={{ id: 'chevron' }}
        title={title}
      >
        <div className="columns-3 space-y-m py-s">
          {links.map(({ href, label }) => {
            return (
              <div
                key={label}
                className="text-body2Medium text-text-link-action"
              >
                <a href={slugify(href)}>{label}</a>
              </div>
            );
          })}
        </div>
      </Accordion>
    </div>
  );
};

interface RelatedLinksProps {
  filters: FilterType;
  modelsByMake: Record<string, string[]>;
  seoLocation: SeoLocation | null;
  topCities: City[];
  citiesInSameState: City[];
}

export const RelatedLinks: React.FC<RelatedLinksProps> = ({
  filters,
  modelsByMake,
  seoLocation,
  topCities,
  citiesInSameState,
}) => {
  const filteredMake = filters.make.length === 1 ? filters.make[0] : null;
  const filteredModel = filters.model.length === 1 ? filters.model[0] : null;

  const conditionSlug = filters.condition === 'Used' ? 'used-cars' : 'new-cars';

  return (
    <ResponsiveWrapper>
      <div className="mx-auto flex max-w-[980px] flex-col gap-xl">
        {/* if make, we show “same make by popular cities” and “same make by different models” */}
        {filteredMake && !filteredModel && !seoLocation ? (
          <>
            <RelatedLinkSection
              title={`${filteredMake} by models`}
              links={(modelsByMake?.[filteredMake] ?? []).map((model) => ({
                href: `/${conditionSlug}/${filteredMake}/${model}`,
                label: `${filteredMake} ${model}`,
              }))}
            />
            <RelatedLinkSection
              title={`${filteredMake} by models`}
              links={(modelsByMake?.[filteredMake] ?? []).map((model) => ({
                href: `/${conditionSlug}/${filteredMake}/${model}`,
                label: `${filteredMake} ${model}`,
              }))}
            />
            <RelatedLinkSection
              title={`${filteredMake} by cities`}
              links={topCities.map((city) => ({
                href: `/${conditionSlug}/${filteredMake}/${city.slug}`,
                label: `${filteredMake} in ${city.name}, ${city.state.toUpperCase()}`,
              }))}
            />
          </>
        ) : null}

        {/* if make and model, we show “same make same model by popular cities and by year” */}
        {filteredMake && filteredModel && !seoLocation ? (
          <>
            <RelatedLinkSection
              title={`Search ${filteredMake} ${filteredModel} by year`}
              links={Array.from(
                { length: 11 },
                (_, i) => new Date().getFullYear() + 1 - i
              ).map((year) => ({
                href: `/${conditionSlug}/${filteredMake}/${filteredModel}/${year}`,
                label: `${year} ${filteredMake} ${filteredModel}`,
              }))}
            />
            <RelatedLinkSection
              title={`Search ${filteredMake} ${filteredModel} by cities`}
              links={topCities.map((city) => ({
                href: `/${conditionSlug}/${filteredMake}/${filteredModel}/${city.slug}`,
                label: `${filteredMake} ${filteredModel} in ${city.name}, ${city.state.toUpperCase()}`,
              }))}
            />
          </>
        ) : null}

        {/* if make model and location, we show “find {{make}} models in {{seoLocation}} and Search {{make}}
         {{model}} by city" */}
        {filteredMake && filteredModel && seoLocation ? (
          <>
            <RelatedLinkSection
              title={`Find ${filteredMake} models in ${seoLocation.title}`}
              links={(modelsByMake?.[filteredMake] ?? []).map((model) => ({
                href: `/${conditionSlug}/${filteredMake}/${model}/${locationSlug(seoLocation.data)}`,
                label: `${filteredMake} ${model} in ${seoLocation.title}`,
              }))}
            />
            <RelatedLinkSection
              title={`Search ${filteredMake} ${filteredModel} by city`}
              links={topCities.map((city) => ({
                href: `/${conditionSlug}/${filteredMake}/${filteredModel}/${city.slug}`,
                label: `${filteredMake} ${filteredModel} in ${city.name}, ${city.state.toUpperCase()}`,
              }))}
            />
          </>
        ) : null}

        {/* if location, we show “popular makes in the same location" and “evs in different cities in same state” */}
        {!filteredMake && !filteredModel && seoLocation ? (
          <>
            <RelatedLinkSection
              title={`Popular makes in ${seoLocation.title}`}
              links={POPULAR_MAKES_EXTENDED.map((make) => ({
                href: `/${conditionSlug}/${make}/${locationSlug(seoLocation.data)}`,
                label: `${make} in ${seoLocation.title}`,
              }))}
            />
            <RelatedLinkSection
              title={`Search EVs by city in ${isCity(seoLocation.data) ? seoLocation.data.state.toUpperCase() : seoLocation.title}`}
              links={citiesInSameState?.map((city) => ({
                href: `/${conditionSlug}/${city.slug}`,
                label: `${city.name}, ${city.state.toUpperCase()}`,
              }))}
            />
          </>
        ) : null}

        {/* if no make, no model, no location, we show "popular makes", "popular cities", and "popular models" */}
        {!filteredMake && !filteredModel && !seoLocation ? (
          <>
            <RelatedLinkSection
              title={`Search popular EVs by make`}
              links={POPULAR_MAKES_EXTENDED.map((make) => ({
                href: `/${conditionSlug}/${make}`,
                label: make,
              }))}
            />
            <RelatedLinkSection
              title={`Search popular EVs by city`}
              links={topCities.map((city) => ({
                href: `/${conditionSlug}/${city.slug}`,
                label: `${city.name}, ${city.state.toUpperCase()}`,
              }))}
            />
            <RelatedLinkSection
              title={`Search popular EVs by model`}
              links={POPULAR_EV_MODELS.map((model) => ({
                href: `/${conditionSlug}/${model.make}/${model.model}`,
                label: model.displayName,
              }))}
            />
          </>
        ) : null}
      </div>

      {/* if only make, we show “{{make}} by models” and “{{make}} by cities” */}
      {filteredMake && !filteredModel && seoLocation ? (
        <>
          <RelatedLinkSection
            title={`Search ${filteredMake} by models`}
            links={(modelsByMake?.[filteredMake] ?? []).map((model) => ({
              href: `/${conditionSlug}/${filteredMake}/${model}`,
              label: `${filteredMake} ${model}`,
            }))}
          />
          <RelatedLinkSection
            title={`Search ${filteredMake} by cities`}
            links={topCities.map((city) => ({
              href: `/${conditionSlug}/${filteredMake}/${city.slug}`,
              label: `${filteredMake} in ${city.name}, ${city.state.toUpperCase()}`,
            }))}
          />
        </>
      ) : null}
    </ResponsiveWrapper>
  );
};
